import { asInteger, asRecord, asString } from "../document/validate.ts";
import { presetNameProblem, type BehaviourPreset } from "./presets.ts";
import { fileText, plainBehaviour, readBehaviour, readTileRef } from "./template-file.ts";

/**
 * The preset file (P1.6-T34, ADR-070's amendment "behaviour presets"): one preset per file, plain JSON with a
 * schema version, written with a fixed key order and sorted maps so equal presets are equal bytes, and read
 * back with every value checked, the first problem named by its path. The behaviour is written and read by
 * the template file's rules (ADR-066 decision 4); its frames are checked against a world only when the preset
 * is listed there, since a preset belongs to no world.
 */

/**
 * The layout this build writes and reads.
 */
export const PRESET_SCHEMA_VERSION = 1;

/**
 * Writes a preset: the schema version, the name, the frame and the behaviour, in that order.
 *
 * @param preset The preset.
 * @returns The file's text, two-space indented, ending in a newline.
 * @throws Error when a behaviour field is named `__proto__`.
 */
export function serialisePreset(preset: BehaviourPreset): string {
  return fileText({
    schemaVersion: PRESET_SCHEMA_VERSION,
    name: preset.name,
    frame: { tileset: preset.frame.tileset, tile: preset.frame.tile },
    behaviour: plainBehaviour(preset.behaviour, "behaviour"),
  });
}

/**
 * Reads a preset.
 *
 * @param text The file's text.
 * @returns The preset.
 * @throws Error naming the path of the first problem: not JSON, another schema, a bad name, a malformed frame
 *   or a missing or malformed behaviour.
 */
export function deserialisePreset(text: string): BehaviourPreset {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("a preset file must be JSON");
  }
  const record = asRecord(parsed, "preset");
  const version = asInteger(record["schemaVersion"], "preset.schemaVersion");
  if (version !== PRESET_SCHEMA_VERSION) {
    throw new Error(`preset.schemaVersion: presets of schema ${version} are not read by this build, which reads schema ${PRESET_SCHEMA_VERSION}`);
  }
  const name = asString(record["name"], "preset.name");
  const problem = presetNameProblem(name);
  if (problem !== null) {
    throw new Error(`preset.name: ${problem}`);
  }
  return { name, frame: readTileRef(record["frame"], "preset.frame"), behaviour: readBehaviour(record["behaviour"], "preset.behaviour") };
}
